import { storeSelector, usePolicyContext } from '@Policy/store';
import { ExchangeOnlinePolicyItem } from '@Policy/utils/types';
import { Stack } from '@tonic-one/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CorrelatedRuleShallow,
  RuleType,
} from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/types';
import { ActionTableHeader } from '../../../Components/Actions';
import { RuleSelection } from './RuleSelection';
import { rulesListLinks } from '../shared';
import { useRiskAIRules } from '../hooks/useRiskAIRules';

export const RuleExclusionList = (props: {
  rules: CorrelatedRuleShallow[];
}) => {
  const { rules } = props;
  const { t } = useTranslation();
  const contextStore = usePolicyContext();
  const contextSelector = storeSelector(contextStore);
  const { setRiskAiState, raiAnomalyExpRules, createMergedRiskAiFilterRule } =
    contextSelector((s) => {
      const template = s.template as ExchangeOnlinePolicyItem;
      return {
        setRiskAiState: s.setRiskAiState,
        raiAnomalyExpRules: template.tmRiskAiFilters.raiAnomalyExpRules,
        createMergedRiskAiFilterRule: s.createMergedRiskAiFilterRule,
      };
    });

  const defaultSelectedIds = useMemo(() => {
    if (!raiAnomalyExpRules || !raiAnomalyExpRules.split) return [];
    return raiAnomalyExpRules.split(',');
  }, [raiAnomalyExpRules]);

  const exceptionRules = useMemo(() => {
    return rules
      .filter(({ raiRuleId }) => defaultSelectedIds.includes(raiRuleId))
      .map((rule) => createMergedRiskAiFilterRule(rule));
  }, [rules, defaultSelectedIds, createMergedRiskAiFilterRule]);

  const { preDefinedRules, selectedPreDefinedRules, setSelectedBuiltInRules } =
    useRiskAIRules({
      rules,
      policyRules: exceptionRules,
      onRulesUpdate: (rules) =>
        setRiskAiState({
          raiAnomalyExpRules: rules.map(({ raiRuleId }) => raiRuleId).join(','),
        }),
    });

  return (
    <Stack gap="4x">
      <ActionTableHeader>{t('policy.riskAi.exclusionList')}</ActionTableHeader>
      <RuleSelection
        allRules={preDefinedRules}
        selectedRules={selectedPreDefinedRules}
        description={t(
          'policy.riskAi.anomalies.exceptionRules.preDefined.desc'
        )}
        onSelectionChange={setSelectedBuiltInRules}
        editableActions={false}
        rulesListLink={rulesListLinks[RuleType.PreDefined]}
      />
    </Stack>
  );
};
